import { X } from 'lucide-react';
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  type InputHTMLAttributes,
  type ReactNode,
} from 'react';

import { Button } from './button';

const hasInputValue = (value: unknown): boolean => value !== undefined && value !== null && String(value).length > 0;

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  clearable?: boolean;
  clearLabel?: string;
  fieldClassName?: string;
  icon?: ReactNode;
  label?: ReactNode;
  onClear?: () => void;
  wrapperClassName?: string;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      clearable = false,
      clearLabel = 'Clear input',
      className = '',
      defaultValue,
      disabled,
      fieldClassName = '',
      icon,
      label,
      onChange,
      onClear,
      readOnly,
      value,
      wrapperClassName = '',
      ...props
    },
    forwardedRef,
  ) => {
    const inputRef = useRef<HTMLInputElement>(null);
    const [hasValue, setHasValue] = useState(() => hasInputValue(value ?? defaultValue));

    useImperativeHandle(forwardedRef, () => inputRef.current as HTMLInputElement);

    useEffect(() => {
      if (value !== undefined) {
        setHasValue(hasInputValue(value));
      }
    }, [value]);

    const clear = (): void => {
      if (disabled || readOnly) {
        return;
      }
      if (value === undefined && inputRef.current) {
        inputRef.current.value = '';
      }
      setHasValue(false);
      onClear?.();
      inputRef.current?.focus();
    };

    const control = (
      <div className={`input-shell ${wrapperClassName}`.trim()}>
        {icon}
        <input
          {...props}
          ref={inputRef}
          className={className || undefined}
          defaultValue={defaultValue}
          disabled={disabled}
          readOnly={readOnly}
          value={value}
          onChange={(event) => {
            setHasValue(hasInputValue(event.currentTarget.value));
            onChange?.(event);
          }}
        />
        {clearable && hasValue && !disabled && !readOnly ? (
          <Button
            iconOnly
            className="size-7! min-h-7! min-w-7! max-h-7! max-w-7! border-0!"
            aria-label={clearLabel}
            onPointerDown={(event) => event.preventDefault()}
            onClick={clear}
          >
            <X className="size-4!" />
          </Button>
        ) : null}
      </div>
    );
    return label === undefined ? (
      control
    ) : (
      <label className={`grid gap-2 ${fieldClassName}`.trim()}>
        <span className="m-0 block text-[11px] font-black text-(--muted)">{label}</span>
        {control}
      </label>
    );
  },
);

Input.displayName = 'Input';
